import { useState, useEffect, useContext } from "react";

import { editObjectData } from "@aclymatepackages/array-immutability-helpers";

import { fetchInternalApi } from "../utils/apiCalls";
import { useAuth, unpackFirebaseObj } from "../firebase";

import usePlatformLayoutContext from "./platformLayout";
import { formatExternalCompanyData } from "./helpers";
import { StripeCustomerContext } from "./stripeCustomerContext";
import { AdminDataContext } from "./adminDataContext";

export { AdminDataContext };

const AdminDataContextProvider = ({ children }) => {
  const { refetchCustomerSubscriptions } = useContext(StripeCustomerContext);
  const { setViewMode } = usePlatformLayoutContext();

  const [user, userLoading] = useAuth();

  const [isUserAclymateAdmin, setIsUserAclymateAdmin] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState({});
  const [selectableCompanies, setSelectableCompanies] = useState([]);
  const [adminData, setAdminData] = useState({
    companyData: {},
    transactions: [],
    offices: [],
    employees: [],
    vehicles: [],
    offsets: [],
    events: [],
    vendors: [],
    tasks: [],
    "audit-logs": [],
    "vendor-relationships": []
  });
  const [isAdminDataLoading, setIsAdminDataLoading] = useState(false);

  const editAdminData = (field, value) =>
    editObjectData(setAdminData, field, value);

  useEffect(() => {
    const checkIfUserIsAclymateAdmin = async () => {
      if (!user) {
        return;
      }

      const { claims } = await user.getIdTokenResult({ forceRefresh: true });

      const { isAclymateAdmin = false } = claims;

      return setIsUserAclymateAdmin(isAclymateAdmin);
    };

    if (!userLoading) {
      checkIfUserIsAclymateAdmin();
    }
  }, [user, userLoading]);

  useEffect(() => {
    const fetchAndSetSelectableCompanies = async () => {
      // adminFetchAllCompanies only accepts prod renew-west tokens (admin
      // checks deliberately never verify demo/staging/test tokens), so this
      // 401s by design on any non-prod environment. fetchInternalApi throws
      // on a non-2xx response, so catch it and degrade to an empty list
      // rather than crash; the dropdown this feeds just stays empty, which
      // is recoverable (Sentry 7796858217).
      try {
        const allCompanies = await fetchInternalApi({
          endpoint: "adminFetchAllCompanies",
          user
        });

        if (!Array.isArray(allCompanies)) {
          return setSelectableCompanies([]);
        }

        const allCompaniesWithNames = allCompanies
          .filter(({ name }) => !!name)
          .map(({ name, id }) => ({
            name: `${name} (${id})`,
            id
          }));

        return setSelectableCompanies(allCompaniesWithNames);
      } catch {
        return setSelectableCompanies([]);
      }
    };

    if (isUserAclymateAdmin) {
      fetchAndSetSelectableCompanies();
    }
  }, [isUserAclymateAdmin, user]);

  const fetchAndSetAdminData = async () => {
    setIsAdminDataLoading(true);

    const {
      companyData = {},
      employees = [],
      offices = [],
      transactions = [],
      vehicles = [],
      offsets = [],
      events = [],
      vendors = [],
      tasks = [],
      auditLogs = [],
      vendorRelationships = [],
      allAggregatedTransactions = [],
      matchedVendorsAggregatedTransactions = [],
      employeesAggregatedTransactions = [],
      eventsAggregatedTransactions = []
    } = await fetchInternalApi({
      endpoint: "adminFetchCompanyData",
      user,
      body: { companyId: selectedCompany.id }
    });
    const { stripeCustomerId, name: selectedCompanyName } = companyData;
    refetchCustomerSubscriptions(stripeCustomerId);

    const { formattedEmployees, formattedTransactions } =
      formatExternalCompanyData({
        companyId: selectedCompany.id,
        companyName: selectedCompanyName,
        employees,
        transactions
      });

    setAdminData({
      companyData: unpackFirebaseObj(companyData),
      employees: formattedEmployees,
      offices,
      transactions: formattedTransactions,
      vehicles,
      offsets,
      events,
      vendors,
      tasks,
      "audit-logs": auditLogs,
      "vendor-relationships": vendorRelationships,
      "all-aggregated-transactions": allAggregatedTransactions,
      "matched-vendors-aggregated-transactions":
        matchedVendorsAggregatedTransactions,
      "employees-aggregated-transactions": employeesAggregatedTransactions,
      "events-aggregated-transactions": eventsAggregatedTransactions
    });

    setIsAdminDataLoading(false);

    return setViewMode("admin");
  };

  return (
    <AdminDataContext.Provider
      value={{
        isUserAclymateAdmin,
        selectedCompany,
        setSelectedCompany,
        selectableCompanies,
        fetchAndSetAdminData,
        isAdminDataLoading,
        adminData,
        editAdminData
      }}
    >
      {children}
    </AdminDataContext.Provider>
  );
};
export default AdminDataContextProvider;
